import 'ui/form-group/form-group';

import { uniqueId } from 'cadenza/utils/unique-id';

import h from 'hyperscript';

import { ColorPicker } from 'ui/color-picker/color-picker';

import {
  opacityAsFractionToTransparencyAsPercent,
  transparencyAsPercentToOpacityAsFraction
} from 'cadenza/map/layer-configuration-panel/layer-config-utils';
import {
  DEFAULT_COLOR_PALETTE,
  toHexColor,
  STRONG_COLOR_PALETTE,
  toRgbaColor
} from 'cadenza/utils/colors/colors';

import 'ui/fieldset/legacy-fieldset';
import { SliderNumberInput } from 'ui/slider-number-input/slider-number-input';
import { Switch } from 'ui/switch/switch';
import { Select } from 'ui/select/select';

import { debounce } from 'cadenza/utils/debounce';

import './symbolism-configuration.css';
import i18n from './symbolism-configuration.properties';

const COMPONENT_NAME = 'd-symbolism-config';

// ids in net.disy.gis.core.label.LabelOffset.LabelOffsetUnit
const OFFSET_UNITS = [ 'pixels', 'meters' ];

// ids in de.disy.cadenza.core.util.position.Anchor
const POSITIONS = [
  'NORTH_WEST', 'NORTH', 'NORTH_EAST',
  'WEST', 'CENTER', 'EAST',
  'SOUTH_WEST', 'SOUTH', 'SOUTH_EAST'
];

export class SymbolismConfiguration extends HTMLElement {

  _model;
  _labelModel;

  _transparencyInput;
  _textColorInput;
  _pointSizeInput;

  _labelEnabledInput;
  _labelFontSizeInput;
  _labelFontColorInput;
  _labelAnchorInput;
  _labelOffsetUnit;
  _labelHorizontalOffsetInput;
  _labelVerticalOffsetInput;

  constructor (model, labelModel) {
    super();
    this._model = model;
    this._labelModel = labelModel;
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    this._render();
  }

  /**
   * @return {SymbolismDto}
   */
  get state () {
    return {
      foregroundOpacity: this._transparencyInput ? transparencyAsPercentToOpacityAsFraction(this._transparencyInput.value) : undefined,
      foregroundColor: this._textColorInput ? toRgbaColor(this._textColorInput.value) : undefined,
      pointSize: this._pointSizeInput ? this._pointSizeInput.value : undefined
    };
  }

  /**
   * @return {LabelDto}
   */
  get labelState () {
    if (!this._labelModel) {
      return null;
    }
    return {
      enabled: this._labelEnabledInput.checked,
      fontColor: toRgbaColor(this._labelFontColorInput.value),
      fontSize: this._labelFontSizeInput.value,
      anchor: this._labelAnchorInput.selection[0].id,
      offset: {
        distanceUnit: this._labelOffsetUnit.selection[0].id,
        horizontal: this._labelHorizontalOffsetInput.value,
        vertical: this._labelVerticalOffsetInput.value
      }
    };
  }

  _render () {
    this.innerHTML = '';
    if (this._model.foregroundOpacity != null) {
      const transparency = opacityAsFractionToTransparencyAsPercent(this._model.foregroundOpacity);
      this._transparencyInput = new SliderNumberInput({
        min: 0,
        max: 100,
        value: transparency,
        unit: '%',
        required: false,
        emitEventOnEveryChange: true
      });
      this.append(
        h('form-group', [
          h('label', i18n('transparency')),
          this._transparencyInput
        ])
      );
    }
    if (this._model.foregroundColor != null) {
      const labelId = uniqueId();
      this._textColorInput = new ColorPicker(STRONG_COLOR_PALETTE);
      this._textColorInput.setAttribute('aria-labelledby', labelId);
      this._textColorInput.value = toHexColor(this._model.foregroundColor);
      this.append(
        h('.form-group', [
          h('label', { id: labelId }, i18n('color')),
          this._textColorInput
        ])
      );
    }

    if (this._model.pointSize) {
      const pointSizeId = uniqueId();
      this._pointSizeInput = h('input.form-control', {
        type: 'number',
        step: 'any',
        min: 1
      });
      this._pointSizeInput.setAttribute('aria-labelledby', pointSizeId);
      this._pointSizeInput.value = this._model.pointSize;
      this._pointSizeInput.addEventListener('input', debounce(this._dispatchChangeEvent));
      this.append(
        h('.form-group', [
          h('label', { id: pointSizeId }, i18n('pointSize')),
          this._pointSizeInput
        ])
      );
    }

    if (this._labelModel) {
      const labelActiveId = uniqueId();
      const labelFieldset = this._createLabelFieldset();

      this._labelEnabledInput = new Switch({ checked: this._labelModel.enabled });
      this._labelEnabledInput.classList.add('float-left');
      const updateLabelFieldset = () => {
        if (this._labelEnabledInput.checked) {
          labelFieldset.classList.remove('hidden');
        } else {
          labelFieldset.classList.add('hidden');
        }
      };
      this._labelEnabledInput.addEventListener('change', updateLabelFieldset);
      updateLabelFieldset();

      this.append(
        h('.form-group', [
          this._labelEnabledInput,
          h('label.label-enabled--label', { id: labelActiveId }, i18n('label.active'))
        ])
      );
      this.append(labelFieldset);
    }
  }

  _createLabelFieldset () {
    const fontColorId = uniqueId();
    const fontSizeId = uniqueId();
    const anchorId = uniqueId();
    const offsetUnitLabelId = uniqueId();
    const labelHorizontalOffsetId = uniqueId();
    const labelVerticalOffsetId = uniqueId();

    this._labelFontColorInput = new ColorPicker(DEFAULT_COLOR_PALETTE);
    this._labelFontColorInput.setAttribute('aria-labelledby', fontColorId);
    this._labelFontColorInput.value = toHexColor(this._labelModel.fontColor);

    this._labelFontSizeInput = h('input.form-control', {
      type: 'number'
    });
    this._labelFontSizeInput.setAttribute('aria-labelledby', fontSizeId);
    this._labelFontSizeInput.value = this._labelModel.fontSize;
    this._labelFontSizeInput.addEventListener('input', debounce(this._dispatchChangeEvent));

    this._labelAnchorInput = new Select({
      data: POSITIONS.map(id => ({
        id,
        text: i18n(`label.position.${id}`),
        selected: id === this._labelModel.anchor
      }))
    });
    this._labelAnchorInput.setAttribute('aria-labelledby', anchorId);

    this._labelOffsetUnit = new Select({
      data: OFFSET_UNITS.map(id => ({
        id,
        text: i18n(`label.offset.unit.${id}`),
        selected: id === this._labelModel.offset.distanceUnit
      }))
    });
    this._labelOffsetUnit.setAttribute('aria-labelledby', offsetUnitLabelId);
    this._labelOffsetUnit.value = this._labelModel.offset.distanceUnit;
    this._labelOffsetUnit.addEventListener('input', debounce(this._dispatchChangeEvent));

    this._labelHorizontalOffsetInput = h('input.form-control', {
      type: 'number',
      step: 'any'
    });
    this._labelHorizontalOffsetInput.setAttribute('aria-labelledby', labelHorizontalOffsetId);
    this._labelHorizontalOffsetInput.value = this._labelModel.offset.horizontal;
    this._labelHorizontalOffsetInput.addEventListener('input', debounce(this._dispatchChangeEvent));

    this._labelVerticalOffsetInput = h('input.form-control', {
      type: 'number',
      step: 'any'
    });
    this._labelVerticalOffsetInput.setAttribute('aria-labelledby', labelVerticalOffsetId);
    this._labelVerticalOffsetInput.value = this._labelModel.offset.vertical;
    this._labelVerticalOffsetInput.addEventListener('input', debounce(this._dispatchChangeEvent));

    return h('d-legacy-fieldset.label-fieldset', [
      h('.form-group', [
        h('label', { id: fontColorId }, i18n('label.font.color')),
        this._labelFontColorInput
      ]),
      h('.form-group', [
        h('label', { id: fontSizeId }, i18n('label.font.size')),
        this._labelFontSizeInput
      ]),
      h('.form-group', [
        h('label', { id: anchorId }, i18n('label.anchor')),
        this._labelAnchorInput
      ]),
      h('.form-group', [
        h('label', { id: anchorId }, i18n('label.offset.unit')),
        this._labelOffsetUnit
      ]),
      h('.form-group', [
        h('label', { id: labelHorizontalOffsetId }, i18n('label.offset.horizontal')),
        this._labelHorizontalOffsetInput
      ]),
      h('.form-group', [
        h('label', { id: labelVerticalOffsetId }, i18n('label.offset.vertical')),
        this._labelVerticalOffsetInput
      ])
    ]);
  }

  _dispatchChangeEvent () {
    this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

}

customElements.define(COMPONENT_NAME, SymbolismConfiguration);
